'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface ForecastInputs {
  currentHeadcount: number;
  currentRevenue: number;
  revenueGrowthRate: number;
  targetRevenuePerEngineer: number;
  timeHorizonMonths: number;
  attritionRate: number;
  averageSalary: number;
  averageTimeToHire: number; // weeks
  rampUpTime: number; // months
}

interface RoleBreakdown {
  frontend: number;
  backend: number;
  fullstack: number;
  mobile: number;
  devops: number;
  qa: number;
  data: number;
  managers: number;
}

export default function HeadcountForecastingPage() {
  const [inputs, setInputs] = useState<ForecastInputs>({
    currentHeadcount: 10,
    currentRevenue: 1000000,
    revenueGrowthRate: 20,
    targetRevenuePerEngineer: 250000,
    timeHorizonMonths: 12,
    attritionRate: 15,
    averageSalary: 130000,
    averageTimeToHire: 8,
    rampUpTime: 3,
  });
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'headcount-forecasting',
    toolCategory: 'calculator',
  });

  const updateInput = <K extends keyof ForecastInputs>(key: K, value: ForecastInputs[K]) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  };

  const forecast = useMemo(() => {
    const months: Array<{
      month: number;
      projectedRevenue: number;
      requiredHeadcount: number;
      netHiring: number;
      attritionLoss: number;
      actualHeadcount: number;
      revenuePerEngineer: number;
      monthlyCost: number;
      roleBreakdown: RoleBreakdown;
    }> = [];
    const monthlyGrowthRate = Math.pow(1 + inputs.revenueGrowthRate / 100, 1 / 12) - 1;
    const monthlyAttritionRate = inputs.attritionRate / 12 / 100;

    // Role distribution percentages
    const getRoleBreakdown = (headcount: number): RoleBreakdown => {
      const managersNeeded = headcount >= 8 ? Math.ceil(headcount / 7) : 0;
      const engineers = headcount - managersNeeded;

      return {
        backend: Math.round(engineers * 0.30),
        frontend: Math.round(engineers * 0.25),
        fullstack: Math.round(engineers * 0.15),
        mobile: Math.round(engineers * 0.10),
        devops: Math.max(1, Math.round(engineers * 0.10)),
        qa: Math.round(engineers * 0.05),
        data: Math.round(engineers * 0.05),
        managers: managersNeeded,
      };
    };

    for (let month = 0; month <= inputs.timeHorizonMonths; month++) {
      const projectedRevenue: number = inputs.currentRevenue * Math.pow(1 + monthlyGrowthRate, month);
      const requiredHeadcount: number = Math.ceil(projectedRevenue / inputs.targetRevenuePerEngineer);
      const attritionLoss: number = Math.round((month > 0 ? months[month - 1].actualHeadcount : inputs.currentHeadcount) * monthlyAttritionRate);
      const netHiring: number = month === 0 ? 0 : Math.max(0, requiredHeadcount - months[month - 1].actualHeadcount + attritionLoss);
      const actualHeadcount: number = month === 0 ? inputs.currentHeadcount : months[month - 1].actualHeadcount + netHiring - attritionLoss;
      const monthlyCost: number = actualHeadcount * inputs.averageSalary / 12 * 1.3; // 30% overhead
      const roleBreakdown = getRoleBreakdown(actualHeadcount);

      months.push({
        month,
        projectedRevenue,
        requiredHeadcount,
        netHiring,
        attritionLoss,
        actualHeadcount,
        revenuePerEngineer: actualHeadcount > 0 ? projectedRevenue / actualHeadcount : 0,
        monthlyCost,
        roleBreakdown,
      });
    }

    const totalHiring = months.reduce((sum, m) => sum + m.netHiring, 0);
    const totalAttrition = months.reduce((sum, m) => sum + m.attritionLoss, 0);
    const finalHeadcount = months[months.length - 1].actualHeadcount;
    const totalCost = months.reduce((sum, m) => sum + m.monthlyCost, 0);

    // Calculate hiring velocity needed
    const hiresPerMonth = totalHiring / inputs.timeHorizonMonths;
    const weeksToHire = inputs.averageTimeToHire;
    const pipelineSize = Math.ceil(hiresPerMonth * (weeksToHire / 4));

    return {
      months,
      totalHiring,
      totalAttrition,
      finalHeadcount,
      headcountGrowth: finalHeadcount - inputs.currentHeadcount,
      growthRate: ((finalHeadcount - inputs.currentHeadcount) / inputs.currentHeadcount) * 100,
      totalCost,
      averageMonthlyBurn: totalCost / inputs.timeHorizonMonths,
      hiresPerMonth,
      pipelineSize,
    };
  }, [inputs]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  const quarterlyData = useMemo(() => {
    const quarters = [];
    for (let q = 0; q < 4; q++) {
      const monthIndex = Math.min(q * 3, forecast.months.length - 1);
      const data = forecast.months[monthIndex];
      quarters.push({ quarter: q + 1, ...data });
    }
    return quarters;
  }, [forecast]);

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const finalRoleBreakdown = forecast.months[forecast.months.length - 1].roleBreakdown;
    const scores: Record<string, number> = {
      finalHeadcount: forecast.finalHeadcount,
      totalHiring: forecast.totalHiring,
      totalAttrition: forecast.totalAttrition,
      growthRate: forecast.growthRate,
      hiresPerMonth: forecast.hiresPerMonth,
    };

    const hiringPriority: 'high' | 'medium' | 'low' = forecast.hiresPerMonth > 3 ? 'high' : 'medium';
    const recommendations = [
      {
        id: 'hiring-velocity',
        title: `Hire ${forecast.hiresPerMonth.toFixed(1)} engineers per month`,
        priority: hiringPriority,
        description: `Maintain pipeline of ${forecast.pipelineSize} candidates to meet hiring targets`,
      },
      ...(forecast.totalAttrition > forecast.headcountGrowth ? [{
        id: 'attrition-warning',
        title: 'Address high attrition',
        priority: 'high' as const,
        description: `${forecast.totalAttrition} departures vs ${forecast.headcountGrowth} net growth - focus on retention`,
      }] : []),
      ...(forecast.hiresPerMonth > 3 ? [{
        id: 'scale-recruiting',
        title: 'Scale recruiting team',
        priority: 'medium' as const,
        description: `High hiring velocity (${forecast.hiresPerMonth.toFixed(1)}/mo) requires additional recruiting capacity`,
      }] : []),
    ];

    const assessment = await saveResult({
      name: `Headcount Forecast - ${new Date().toLocaleDateString()}`,
      score_overall: forecast.finalHeadcount,
      scores,
      answers: {
        inputs,
      },
      recommendations,
      metadata: {
        currentHeadcount: inputs.currentHeadcount,
        finalHeadcount: forecast.finalHeadcount,
        headcountGrowth: forecast.headcountGrowth,
        growthRate: forecast.growthRate,
        totalHiring: forecast.totalHiring,
        totalAttrition: forecast.totalAttrition,
        hiresPerMonth: forecast.hiresPerMonth,
        pipelineSize: forecast.pipelineSize,
        totalCost: forecast.totalCost,
        averageMonthlyBurn: forecast.averageMonthlyBurn,
        timeHorizonMonths: inputs.timeHorizonMonths,
        roleBreakdown: finalRoleBreakdown,
        quarterlyData: quarterlyData.map(q => ({
          quarter: q.quarter,
          headcount: q.actualHeadcount,
          revenue: q.projectedRevenue,
          hiring: q.netHiring,
          attrition: q.attritionLoss,
        })),
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">📊</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Headcount Forecasting Tool</h1>
          </div>
          <p className="text-xl text-foreground-secondary">Project future headcount needs based on growth and attrition</p>
        </div>

        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">Home</Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Headcount Forecasting</span>
        </nav>

        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this tool</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Enter current headcount and revenue metrics</li>
            <li>Set growth rate and revenue per engineer targets</li>
            <li>Adjust attrition rate based on historical data</li>
            <li>Review monthly and quarterly projections</li>
          </ol>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Current State</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Current Headcount</label>
                  <input type="number" inputMode="numeric" min="1" value={inputs.currentHeadcount}
                    onChange={(e) => updateInput('currentHeadcount', parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Current Annual Revenue</label>
                  <input type="number" inputMode="numeric" min="0" step="100000" value={inputs.currentRevenue}
                    onChange={(e) => updateInput('currentRevenue', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Growth Assumptions</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Revenue Growth Rate: {inputs.revenueGrowthRate}%/year</label>
                  <input type="range" min="0" max="100" value={inputs.revenueGrowthRate}
                    onChange={(e) => updateInput('revenueGrowthRate', parseInt(e.target.value))}
                    className="w-full" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Target Revenue per Engineer</label>
                  <input type="number" inputMode="numeric" min="50000" step="25000" value={inputs.targetRevenuePerEngineer}
                    onChange={(e) => updateInput('targetRevenuePerEngineer', parseInt(e.target.value) || 50000)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Attrition Rate: {inputs.attritionRate}%/year</label>
                  <input type="range" min="0" max="30" value={inputs.attritionRate}
                    onChange={(e) => updateInput('attritionRate', parseInt(e.target.value))}
                    className="w-full" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Forecast Horizon: {inputs.timeHorizonMonths} months</label>
                  <input type="range" min="6" max="36" step="6" value={inputs.timeHorizonMonths}
                    onChange={(e) => updateInput('timeHorizonMonths', parseInt(e.target.value))}
                    className="w-full" />
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Hiring Assumptions</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Average Engineer Salary</label>
                  <input type="number" inputMode="numeric" min="50000" step="10000" value={inputs.averageSalary}
                    onChange={(e) => updateInput('averageSalary', parseInt(e.target.value) || 50000)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Average Time to Hire: {inputs.averageTimeToHire} weeks</label>
                  <input type="range" min="4" max="16" value={inputs.averageTimeToHire}
                    onChange={(e) => updateInput('averageTimeToHire', parseInt(e.target.value))}
                    className="w-full" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Ramp-up Time: {inputs.rampUpTime} months</label>
                  <input type="range" min="1" max="6" value={inputs.rampUpTime}
                    onChange={(e) => updateInput('rampUpTime', parseInt(e.target.value))}
                    className="w-full" />
                  <p className="text-xs text-foreground-tertiary mt-1">Time for new hires to reach full productivity</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Forecast Summary</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Final Headcount</div>
                  <div className="text-3xl font-bold text-accent">{forecast.finalHeadcount}</div>
                </div>
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Growth</div>
                  <div className="text-3xl font-bold text-green-400">+{forecast.headcountGrowth}</div>
                </div>
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Total Hiring</div>
                  <div className="text-2xl font-bold text-blue-400">{forecast.totalHiring}</div>
                </div>
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Attrition</div>
                  <div className="text-2xl font-bold text-red-400">{forecast.totalAttrition}</div>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Quarterly Breakdown</h3>
              <div className="space-y-3">
                {quarterlyData.map((q) => (
                  <div key={q.quarter} className="p-4 bg-background rounded border border-border-muted">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-bold text-foreground">Q{q.quarter}</span>
                      <span className="text-accent font-bold">{q.actualHeadcount} engineers</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-foreground-secondary">
                      <div>Revenue: {formatCurrency(q.projectedRevenue)}</div>
                      <div>$/Engineer: {formatCurrency(q.revenuePerEngineer)}</div>
                      <div>Hiring: +{q.netHiring}</div>
                      <div>Attrition: -{q.attritionLoss}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Role Breakdown (Final State)</h3>
              <div className="space-y-2">
                {Object.entries(forecast.months[forecast.months.length - 1].roleBreakdown).map(([role, count]) => (
                  count > 0 && (
                    <div key={role} className="flex justify-between items-center text-sm">
                      <span className="text-foreground-secondary capitalize">{role}</span>
                      <span className="text-accent font-bold">{count}</span>
                    </div>
                  )
                ))}
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Hiring Velocity</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-background rounded">
                  <span className="text-foreground-secondary">Hires per Month</span>
                  <span className="text-accent font-bold">{forecast.hiresPerMonth.toFixed(1)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-background rounded">
                  <span className="text-foreground-secondary">Pipeline Size Needed</span>
                  <span className="text-accent font-bold">{forecast.pipelineSize} candidates</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-background rounded">
                  <span className="text-foreground-secondary">Total Cost</span>
                  <span className="text-accent font-bold">{formatCurrency(forecast.totalCost)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-background rounded">
                  <span className="text-foreground-secondary">Avg Monthly Burn</span>
                  <span className="text-accent font-bold">{formatCurrency(forecast.averageMonthlyBurn)}</span>
                </div>
              </div>
            </div>

            <div className="bg-accent-muted border border-border rounded-lg p-6">
              <h3 className="text-lg font-bold text-accent mb-3">Key Insights</h3>
              <ul className="space-y-2 text-sm text-foreground-secondary">
                <li>• Headcount will grow {forecast.growthRate.toFixed(0)}% over {inputs.timeHorizonMonths} months</li>
                <li>• Need to hire {forecast.totalHiring} engineers to account for growth and attrition</li>
                <li>• Average {forecast.hiresPerMonth.toFixed(1)} hires per month, {forecast.pipelineSize} candidates in pipeline</li>
                <li>• Total cost over period: {formatCurrency(forecast.totalCost)}</li>
                {forecast.totalAttrition > forecast.headcountGrowth && (
                  <li>• High attrition: {forecast.totalAttrition} departures vs {forecast.headcountGrowth} net growth</li>
                )}
                {forecast.hiresPerMonth > 3 && (
                  <li>• High hiring velocity ({forecast.hiresPerMonth.toFixed(1)}/mo) - consider scaling recruitment team</li>
                )}
              </ul>
            </div>

            {/* Command Center Save Button */}
            <div className="flex flex-wrap gap-4">
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                  {saveError}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
